import {
  useDeleteCompetitionRegistrationMutation,
  usePartialUpdateCompetitionRegistrationMutation,
} from '../mutations';
import { ConfirmDialog } from '~/components/ConfirmDialog/ConfirmDialog';
import { ContextMenu } from '~/components/ContextMenu/ContextMenu';
import { useCompetitionRegistrationListQuery } from '../queries';
import { useAuthentication } from '~/hooks/useAuthentication';
import { useSortableTable } from '~/hooks/useSortableTable';
import { insertOnCondition } from '~/utils/array';
import { formatPoints, teamScoreTotal } from '~/utils/points';
import { Badge } from '~/components/Badge/Badge';
import { Heading } from '~/components/Heading/Heading';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { Table } from '~/components/Table/Table';
import { Link } from '~/components/Link/Link';
import { useDialog } from '~/hooks/useDialog';
import { Fragment } from 'react/jsx-runtime';
import { Registration } from '~/apis/ssf';
import { toast } from 'react-toastify';
import { match } from 'ts-pattern';

type Register = ReturnType<typeof useSortableTable>['register'];

interface Props {
  competitionId: number;
  checkForClubAdmin: boolean;
  isTeamComp?: boolean;
}

interface RowProps {
  registration: Registration;
  checkForClubAdmin: boolean;
}

interface TeamGroup {
  key: string;
  label: string;
  rows: Registration[];
}

function groupRegistrationsByTeam(registrations: Registration[]): TeamGroup[] {
  const groups = new Map<string, TeamGroup>();
  for (const r of registrations) {
    const key = r.team ? `t-${r.team.id}` : 'no-team';
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: r.team ? `${r.team.division.name} — ${r.team.name}` : 'Inget lag',
        rows: [],
      });
    }
    groups.get(key)!.rows.push(r);
  }
  return Array.from(groups.values()).sort((a, b) => teamScoreTotal(b.rows) - teamScoreTotal(a.rows));
}

function Row({ registration, checkForClubAdmin }: RowProps) {
  const { can } = useAuthentication();

  const updateMutation = usePartialUpdateCompetitionRegistrationMutation();
  const deleteMutation = useDeleteCompetitionRegistrationMutation();

  const dialog = useDialog();

  async function handleDelete() {
    await toast.promise(deleteMutation.mutateAsync(registration.id), {
      success: 'Anmälan har tagits bort',
      pending: 'Tar bort anmälan...',
    });
  }

  async function toggleApprove() {
    const isApproving = registration.state !== 'APPROVED';
    await toast.promise(
      updateMutation.mutateAsync({
        id: registration.id,
        payload: { state: isApproving ? 'APPROVED' : 'NOT_APPROVED' },
      }),
      {
        pending: isApproving ? 'Godkänner anmälan...' : 'Tar bort godkännande...',
        success: isApproving ? 'Anmälan godkänd' : 'Godkännande borttaget',
      }
    );
  }

  async function toggleWithdraw() {
    const isWithdrawing = registration.state !== 'WITHDRAWN';
    await toast.promise(
      updateMutation.mutateAsync({
        id: registration.id,
        payload: { state: isWithdrawing ? 'WITHDRAWN' : 'NOT_APPROVED' },
      }),
      {
        pending: isWithdrawing ? 'Stryker anmälan...' : 'Återställer anmälan...',
        success: isWithdrawing ? 'Anmälan struken' : 'Anmälan återställd',
      }
    );
  }

  return (
    <Fragment>
      <Table.Row strikethrough={registration.state === 'WITHDRAWN'}>
        <Table.Col>
          <Link to={`/lifters/${registration.person.id}`}>
            {registration.person.first_name} {registration.person.last_name}
          </Link>
          {registration.is_reserve && (
            <Tooltip title="Reserv" as="span">
              {' '}
              (R)
            </Tooltip>
          )}
          <small style={{ display: 'block' }}>{registration.person.license_id}</small>
        </Table.Col>
        <Table.Col>
          <Link to={`/clubs/${registration.person.club.id}`}>{registration.person.club.name}</Link>
        </Table.Col>
        <Table.Col>{registration.competition_type?.description ?? '-'}</Table.Col>
        <Table.Col>{registration.age_category?.label ?? '-'}</Table.Col>
        <Table.Col>{registration.weight_class?.name ?? '-'}</Table.Col>
        <Table.Col>
          {registration.nomination_result ? formatPoints(registration.nomination_result) : '-'}
        </Table.Col>
        <Table.Col>
          {registration.weight_class === null ? (
            <Tooltip title="Ingen kvalgräns" as="span">
              <Badge variant="default" label="Ja" />
            </Tooltip>
          ) : (
            match(registration.is_qualified)
              .with(true, () => <Badge variant="success" label="Ja" />)
              .otherwise(() => <Badge variant="danger" label="Nej" />)
          )}
        </Table.Col>

        {can('see-competition-registration-status', registration.competition.id) && (
          <Table.Col>
            {match(registration.state)
              .with('APPROVED', () => <Badge variant="success" label="Godkänd" />)
              .with('WITHDRAWN', () => <Badge variant="warning" label="Struken" />)
              .otherwise(() => (
                <Badge variant="danger" label="Ej godkänd" />
              ))}
          </Table.Col>
        )}

        {(can('manual-approve-registration', registration.competition.id) ||
          can('edit-competition-registration', registration.competition.id, checkForClubAdmin) ||
          can('delete-competition-registration', registration.competition.id, checkForClubAdmin)) && (
          <Table.Col align="right">
            <Table.Context
              items={[
                ...insertOnCondition(
                  <ContextMenu.Item
                    onClick={toggleApprove}
                    label={registration.state === 'APPROVED' ? 'Ta bort godkännande' : 'Godkänn manuellt'}
                    key="toggle-approve"
                  />,
                  can('manual-approve-registration', registration.competition.id) && registration.state !== 'WITHDRAWN'
                ),
                ...insertOnCondition(
                  <ContextMenu.Item onClick={toggleWithdraw} label="Stryk anmälan" key="withdraw" />,
                  can('manual-approve-registration', registration.competition.id) && registration.state !== 'WITHDRAWN'
                ),
                ...insertOnCondition(
                  <ContextMenu.Item onClick={toggleWithdraw} label="Ångra strykning" key="un-withdraw" />,
                  can('manual-approve-registration', registration.competition.id) && registration.state === 'WITHDRAWN'
                ),
                ...insertOnCondition(
                  <ContextMenu.Item
                    label="Redigera"
                    key="edit"
                    to={`/competitions/${registration.competition.id}/registrations/${registration.id}/edit`}
                  />,
                  can('edit-competition-registration', registration.competition.id, checkForClubAdmin)
                ),
                ...insertOnCondition(
                  <ContextMenu.Item label="Ta bort" key="delete" onClick={dialog.open} danger />,
                  can('delete-competition-registration', registration.competition.id, checkForClubAdmin)
                ),
              ]}
            />
          </Table.Col>
        )}
      </Table.Row>

      <ConfirmDialog {...dialog} isLoading={deleteMutation.isPending} onConfirm={handleDelete} />
    </Fragment>
  );
}

function Empty({ cols }: { cols: number }) {
  return (
    <Table.Row>
      <Table.Col colSpan={cols}>
        <div style={{ textAlign: 'center' }}>Din förening har ännu inte anmält några lyftare till denna tävling.</div>
      </Table.Col>
    </Table.Row>
  );
}

interface LifterTableProps {
  registrations: Registration[];
  isLoading?: boolean;
  totalCols: number;
  canSeeStatus: boolean;
  canTakeAction: boolean;
  checkForClubAdmin: boolean;
  register: Register;
}

function LifterTable(props: LifterTableProps) {
  const { registrations, isLoading, totalCols, canSeeStatus, canTakeAction, checkForClubAdmin, register } = props;

  return (
    <Table bg="even">
      <Table.Head>
        <Table.Row>
          <Table.Col as="th" {...register('first_name')}>
            Lyftare
          </Table.Col>
          <Table.Col as="th" {...register('club_name')}>
            Förening
          </Table.Col>
          <Table.Col as="th" {...register('competition_type')}>
            Tävlingstyp
          </Table.Col>
          <Table.Col as="th" {...register('age_category')}>
            Ålderskategori
          </Table.Col>
          <Table.Col as="th" {...register('weight_class')}>
            Viktklass
          </Table.Col>
          <Table.Col as="th" {...register('nomination_result')}>
            Resultat
          </Table.Col>
          <Table.Col as="th">Kvalad</Table.Col>
          {canSeeStatus && <Table.Col as="th">Anmälan</Table.Col>}
          {canTakeAction && <Table.Col as="th" />}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {isLoading ? (
          <Table.Loading cols={totalCols} />
        ) : !registrations.length ? (
          <Empty cols={totalCols} />
        ) : (
          registrations.map(r => <Row registration={r} key={r.id} checkForClubAdmin={checkForClubAdmin} />)
        )}
      </Table.Body>
    </Table>
  );
}

function GroupCount({ total, withdrawn }: { total: number; withdrawn: number }) {
  return (
    <span style={{ color: 'var(--color-text-lighter)', fontWeight: 'normal' }}>
      ({total}st
      {withdrawn > 0 ? `, varav ${withdrawn} ${withdrawn > 1 ? 'strukna' : 'struken'}` : ''})
    </span>
  );
}

export function CompetitionLifterTable({ competitionId, checkForClubAdmin, isTeamComp }: Props) {
  const { register, values } = useSortableTable('-nomination_result');
  const { can } = useAuthentication();

  const { data, isLoading } = useCompetitionRegistrationListQuery({
    competition: competitionId,
    ordering: values.ordering,
    editable: true,
    page_size: 999,
  });

  const canSeeStatus = can('see-competition-registration-status', competitionId);
  const canTakeAction =
    can('manual-approve-registration', competitionId) ||
    can('edit-competition-registration', competitionId, checkForClubAdmin) ||
    can('delete-competition-registration', competitionId, checkForClubAdmin);

  const totalCols = 7 + (canSeeStatus ? 1 : 0) + (canTakeAction ? 1 : 0);

  const sharedProps = { totalCols, canSeeStatus, canTakeAction, checkForClubAdmin, register };

  if (isLoading || !isTeamComp || !data?.results.length) {
    return <LifterTable registrations={data?.results ?? []} isLoading={isLoading} {...sharedProps} />;
  }

  return (
    <Fragment>
      {groupRegistrationsByTeam(data.results).map(group => {
        const withdrawnCount = group.rows.filter(r => r.state === 'WITHDRAWN').length;

        return (
          <Fragment key={group.key}>
            <Heading as="h5">
              {group.label} <GroupCount total={group.rows.length} withdrawn={withdrawnCount} />
            </Heading>
            <LifterTable registrations={group.rows} {...sharedProps} />
          </Fragment>
        );
      })}
    </Fragment>
  );
}
